<div class="rd-modal">

  <!-- Header -->
  <div class="rd-header">
    @if (data(); as r) {
      <div class="rd-header-left">
        <div class="rd-title">
          {{ r.returnNo || '#' + r.returnNumber }}
          <span class="rd-status" [ngClass]="statusClass(r.status)">{{ r.status }}</span>
          @if (r.refundDocType === 'CREDIT_NOTE') {
            <span class="rd-pill info">Credit Note</span>
          }
        </div>
        <div class="rd-subtitle">
          {{ r.returnedAt | date:'medium' }} · Cashier {{ r.cashier?.name || r.cashier?.username }}
          @if (r.customer) { · Customer {{ r.customer.name }} }
        </div>
      </div>
    } @else {
      <div class="rd-header-left">
        <div class="rd-title">Return</div>
      </div>
    }
    <button class="rd-close" (click)="activeModal.dismiss()" type="button">
      <i class="bi bi-x-lg"></i>
    </button>
  </div>

  <!-- Body -->
  @if (isLoading()) {
    <div class="rd-loading">Loading…</div>
  } @else if (data(); as r) {
    <div class="rd-body">

      <!-- ── LEFT: Original bill snapshot ────────────────────────── -->
      <div class="rd-col rd-col-left">
        <h3 class="rd-section-title">Original bill</h3>
        <div class="rd-bill-meta">
          <div>
            <span class="rd-meta-label">Bill #</span>
            <strong>{{ r.originalBilling.billNo || (r.originalBilling.id | slice:0:8) }}</strong>
          </div>
          <div>
            <span class="rd-meta-label">Date</span>
            {{ r.originalBilling.billingDate | date:'medium' }}
          </div>
          <div>
            <span class="rd-meta-label">Total</span>
            ₹{{ asNumber(r.originalBilling.grandTotal) | number:'1.2-2' }}
          </div>
          @if (r.originalBilling.appliedOfferName) {
            <div>
              <span class="rd-meta-label">Offer</span>
              {{ r.originalBilling.appliedOfferName }} ({{ r.originalBilling.appliedOfferType }})
            </div>
          }
        </div>

        <table class="rd-bill-items">
          <thead>
            <tr>
              <th>Item</th>
              <th class="num">Qty</th>
              <th class="num">Price</th>
              <th class="num">Line</th>
            </tr>
          </thead>
          <tbody>
            @for (it of r.originalBilling.products; track it.id) {
              @let returned = isReturnedBillItem(it.id);
              @let retQty = returnedQtyFor(it.id);
              <tr [class.rd-row-returned]="returned">
                <td>
                  <div>{{ it.variant?.product?.name || (it.variant?.id | slice:0:6) }}</div>
                  @if (returned) {
                    <div class="rd-bill-sub">
                      <i class="bi bi-arrow-return-left"></i>
                      Returned: {{ retQty }}
                    </div>
                  }
                </td>
                <td class="num">{{ it.quantity }}</td>
                <td class="num">₹{{ asNumber(it.price) | number:'1.2-2' }}</td>
                <td class="num">₹{{ asNumber(it.total) | number:'1.2-2' }}</td>
              </tr>
            }
          </tbody>
        </table>

        <h4 class="rd-subsection-title">Original tender</h4>
        <div class="rd-tender-list">
          @for (p of r.originalBilling.payments; track p.id) {
            <div class="rd-tender-row">
              <span class="rd-tender-mode">{{ p.mode }}</span>
              <span>₹{{ asNumber(p.amount) | number:'1.2-2' }}</span>
            </div>
          }
        </div>
      </div>

      <!-- ── RIGHT: Return summary + audit timeline + actions ───── -->
      <div class="rd-col rd-col-right">

        <h3 class="rd-section-title">Refund</h3>
        <div class="rd-headline">
          <div class="rd-headline-label">Total refunded</div>
          <div class="rd-headline-value">₹{{ asNumber(r.grandTotalRefunded) | number:'1.2-2' }}</div>
        </div>

        <table class="rd-return-items">
          <thead>
            <tr>
              <th>Item</th>
              <th class="num">Qty</th>
              <th class="num">Refund</th>
              <th>Goes to</th>
            </tr>
          </thead>
          <tbody>
            @for (it of r.items; track it.id) {
              <tr>
                <td>
                  {{ it.variant?.product?.name || (it.variant?.id | slice:0:6) }}
                  @if (it.offerOverrideUsed) {
                    <div class="rd-warn-line">
                      <i class="bi bi-shield-lock"></i>
                      {{ it.offerOverrideNote || 'Offer override applied' }}
                    </div>
                  }
                </td>
                <td class="num">{{ it.quantityReturned }}</td>
                <td class="num">₹{{ asNumber(it.lineRefund) | number:'1.2-2' }}</td>
                <td>{{ it.disposition }}</td>
              </tr>
            }
          </tbody>
        </table>

        <h4 class="rd-subsection-title">Refund tender</h4>
        <div class="rd-tender-list">
          @for (rf of r.refunds; track rf.id) {
            <div class="rd-tender-row">
              <span class="rd-tender-mode">
                {{ rf.mode }}
                @if (rf.asyncState) {
                  <span class="rd-async-pill" [ngClass]="rf.asyncState">{{ rf.asyncState }}</span>
                }
              </span>
              <span>₹{{ asNumber(rf.amount) | number:'1.2-2' }}</span>
            </div>
          }
        </div>

        <!-- Audit timeline -->
        <h4 class="rd-subsection-title">Audit trail</h4>
        <div class="rd-timeline">
          <div class="rd-timeline-row">
            <div class="rd-timeline-dot rd-dot-ok"></div>
            <div class="rd-timeline-body">
              <strong>Cashier initiated</strong>
              <span>{{ r.cashier?.name || r.cashier?.username }} · {{ r.returnedAt | date:'medium' }}</span>
            </div>
          </div>
          @if (r.approver) {
            <div class="rd-timeline-row">
              <div class="rd-timeline-dot rd-dot-ok"></div>
              <div class="rd-timeline-body">
                <strong>Manager approved (on-floor PIN)</strong>
                <span>{{ r.approver.name || r.approver.username }}</span>
              </div>
            </div>
          }
          @if (r.reconciledAt) {
            <div class="rd-timeline-row">
              <div class="rd-timeline-dot" [ngClass]="statusClass(r.status)"></div>
              <div class="rd-timeline-body">
                <strong>Reconciled by back-office</strong>
                <span>{{ r.reconciler?.name || r.reconciler?.username }} · {{ r.reconciledAt | date:'medium' }}</span>
                @if (r.reconciliationReason) {
                  <pre class="rd-timeline-note">{{ r.reconciliationReason }}</pre>
                }
              </div>
            </div>
          } @else if (r.reconciliationReason) {
            <div class="rd-timeline-row">
              <div class="rd-timeline-dot rd-dot-warn"></div>
              <div class="rd-timeline-body">
                <strong>Reconciliation reason</strong>
                <pre class="rd-timeline-note">{{ r.reconciliationReason }}</pre>
              </div>
            </div>
          }
        </div>

        <!-- Inline note panel for reject / escalate / cancel -->
        @if (noteOpen(); as kind) {
          <div class="rd-note-panel">
            <div class="rd-note-head">
              <strong>
                @switch (kind) {
                  @case ('reject') { Reject this return }
                  @case ('escalate') { Escalate to next tier }
                  @case ('cancel') { Cancel this return }
                }
              </strong>
              <button class="rd-link" (click)="closeNote()" type="button">Cancel</button>
            </div>
            <textarea
              [ngModel]="noteText()"
              (ngModelChange)="noteText.set($event)"
              [placeholder]="kind === 'reject' ? 'Why is this being rejected? Customer-facing audit log.' : kind === 'cancel' ? 'Why is this being cancelled?' : 'Why is this being escalated?'"
              maxlength="500"
              rows="4"
            ></textarea>
            <div class="rd-note-actions">
              <button
                class="rd-btn"
                [ngClass]="kind === 'reject' ? 'danger' : kind === 'cancel' ? 'warn' : 'primary'"
                [disabled]="actionState() !== 'idle'"
                (click)="submitNote()"
                type="button"
              >
                @switch (kind) {
                  @case ('reject') { Confirm rejection }
                  @case ('escalate') { Send escalation }
                  @case ('cancel') { Confirm cancel }
                }
              </button>
            </div>
          </div>
        }
      </div>
    </div>

    <!-- Action bar -->
    <div class="rd-footer">
      <button class="rd-btn ghost" (click)="activeModal.dismiss()" type="button">Close</button>
      <div class="rd-footer-spacer"></div>

      @if (isReconcilable(r.status)) {
        <button
          class="rd-btn warn"
          (click)="openNote('escalate')"
          [disabled]="actionState() !== 'idle'"
          type="button"
        >
          <i class="bi bi-arrow-up-right-circle"></i> Escalate
        </button>
        <button
          class="rd-btn danger"
          (click)="openNote('reject')"
          [disabled]="actionState() !== 'idle'"
          type="button"
        >
          <i class="bi bi-x-circle"></i> Reject
        </button>
        <button
          class="rd-btn primary"
          (click)="approve()"
          [disabled]="actionState() !== 'idle'"
          type="button"
        >
          @if (actionState() === 'approving') {
            <i class="bi bi-arrow-repeat rd-spin"></i> Approving…
          } @else {
            <i class="bi bi-check2-circle"></i> Approve
          }
        </button>
      } @else if (isCancellable(r.status)) {
        <button
          class="rd-btn warn"
          (click)="openNote('cancel')"
          [disabled]="actionState() !== 'idle'"
          type="button"
        >
          <i class="bi bi-x-circle"></i> Cancel return
        </button>
      } @else {
        <span class="rd-status-final">
          <i class="bi bi-lock"></i>
          Locked — no further actions.
        </span>
      }
    </div>
  }
</div>
